import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  createDealApi,
  getDealApi,
  listDealsApi,
  updateDealApi,
  deleteDealApi,
} from '../../deal';

/**
 * Consolidated test component for all Deal endpoints
 */
const TestDeals: React.FC = () => {
  // Create Deal state
  const [createOrgId, setCreateOrgId] = useState('');
  const [createTitle, setCreateTitle] = useState('Test Deal');
  const [createDescription, setCreateDescription] = useState('');
  const [createInitialStage, setCreateInitialStage] = useState('drafting');
  const [createResponse, setCreateResponse] = useState<any>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Get Deal state
  const [getDealId, setGetDealId] = useState('');
  const [getResponse, setGetResponse] = useState<any>(null);
  const [getLoading, setGetLoading] = useState(false);
  const [getError, setGetError] = useState<string | null>(null);

  // List Deals state
  const [listOrgId, setListOrgId] = useState('');
  const [listLimit, setListLimit] = useState('20');
  const [listResponse, setListResponse] = useState<any>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Update Deal state
  const [updateDealId, setUpdateDealId] = useState('');
  const [updateTitle, setUpdateTitle] = useState('');
  const [updateDescription, setUpdateDescription] = useState('');
  const [updateResponse, setUpdateResponse] = useState<any>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Delete Deal state
  const [deleteDealId, setDeleteDealId] = useState('');
  const [deleteResponse, setDeleteResponse] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dealStages = ['drafting', 'negotiation', 'signing', 'delivery', 'completed', 'cancelled'];

  // Handlers
  const handleCreateDeal = async () => {
    if (!createOrgId.trim() || !createTitle.trim()) {
      setCreateError('Please enter organization ID and title');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      const result = await createDealApi({
        orgId: createOrgId.trim(),
        title: createTitle.trim(),
        description: createDescription.trim() || undefined,
        initialStage: createInitialStage as any,
      });
      setCreateResponse(result);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleGetDeal = async () => {
    if (!getDealId.trim()) {
      setGetError('Please enter a deal ID');
      return;
    }

    setGetLoading(true);
    setGetError(null);

    try {
      const result = await getDealApi({
        dealId: getDealId.trim(),
      });
      setGetResponse(result);
    } catch (err) {
      setGetError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGetLoading(false);
    }
  };

  const handleListDeals = async () => {
    setListLoading(true);
    setListError(null);

    try {
      const result = await listDealsApi({
        orgId: listOrgId.trim() || undefined,
        limit: parseInt(listLimit) || 20,
      });
      setListResponse(result);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setListLoading(false);
    }
  };

  const handleUpdateDeal = async () => {
    if (!updateDealId.trim()) {
      setUpdateError('Please enter a deal ID');
      return;
    }

    setUpdateLoading(true);
    setUpdateError(null);

    try {
      const result = await updateDealApi({
        dealId: updateDealId.trim(),
        title: updateTitle.trim() || undefined,
        description: updateDescription.trim() || undefined,
      });
      setUpdateResponse(result);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteDeal = async () => {
    if (!deleteDealId.trim()) {
      setDeleteError('Please enter a deal ID');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await deleteDealApi({ dealId: deleteDealId.trim() });
      setDeleteResponse({ message: 'Deal deleted successfully' });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        Deal APIs
      </Typography>

      {/* Create Deal */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Create Deal</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={createOrgId}
              onChange={(e) => setCreateOrgId(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Title"
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Initial Stage</InputLabel>
              <Select
                value={createInitialStage}
                onChange={(e) => setCreateInitialStage(e.target.value)}
                label="Initial Stage"
              >
                {dealStages.map((stage) => (
                  <MenuItem key={stage} value={stage}>
                    {stage}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleCreateDeal}
              disabled={createLoading}
            >
              {createLoading ? <CircularProgress size={24} /> : 'Create Deal'}
            </Button>
            {createResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(createResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {createError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{createError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Get Deal */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Get Deal</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Deal ID"
              value={getDealId}
              onChange={(e) => setGetDealId(e.target.value)}
              fullWidth
              required
            />
            <Button
              variant="contained"
              onClick={handleGetDeal}
              disabled={getLoading}
            >
              {getLoading ? <CircularProgress size={24} /> : 'Get Deal'}
            </Button>
            {getResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(getResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {getError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{getError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* List Deals */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">List Deals</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID (Optional)"
              value={listOrgId}
              onChange={(e) => setListOrgId(e.target.value)}
              fullWidth
              placeholder="Leave empty to list all your deals"
            />
            <TextField
              label="Limit"
              value={listLimit}
              onChange={(e) => setListLimit(e.target.value)}
              fullWidth
              type="number"
            />
            <Button
              variant="contained"
              onClick={handleListDeals}
              disabled={listLoading}
            >
              {listLoading ? <CircularProgress size={24} /> : 'List Deals'}
            </Button>
            {listResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                  Deals ({Object.keys(listResponse.deals || {}).length}):
                </Typography>
                {listResponse.deals && Object.keys(listResponse.deals).length > 0 ? (
                  <List>
                    {Object.values(listResponse.deals).map((deal: any, index: number) => (
                      <React.Fragment key={deal.dealId}>
                        <ListItem>
                          <ListItemText
                            primary={`Deal ID: ${deal.dealId}`}
                            secondary={
                              <>
                                Owner: {deal.ownerUserId}
                                {deal.ownerOrgId && (
                                  <>
                                    <br />
                                    Org ID: {deal.ownerOrgId}
                                  </>
                                )}
                                <br />
                                Created: {new Date(deal.createdAt).toLocaleString()}
                              </>
                            }
                          />
                        </ListItem>
                        {index < Object.keys(listResponse.deals).length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography>No deals found</Typography>
                )}
              </Paper>
            )}
            {listError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{listError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update Deal */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Update Deal</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Deal ID"
              value={updateDealId}
              onChange={(e) => setUpdateDealId(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="New Title (Optional)"
              value={updateTitle}
              onChange={(e) => setUpdateTitle(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Description (Optional)"
              value={updateDescription}
              onChange={(e) => setUpdateDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <Button
              variant="contained"
              onClick={handleUpdateDeal}
              disabled={updateLoading}
            >
              {updateLoading ? <CircularProgress size={24} /> : 'Update Deal'}
            </Button>
            {updateResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(updateResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {updateError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{updateError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Delete Deal */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Delete Deal</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Deal ID"
              value={deleteDealId}
              onChange={(e) => setDeleteDealId(e.target.value)}
              fullWidth
              required
            />
            <Button
              variant="contained"
              onClick={handleDeleteDeal}
              disabled={deleteLoading}
              color="error"
            >
              {deleteLoading ? <CircularProgress size={24} /> : 'Delete Deal'}
            </Button>
            {deleteResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(deleteResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {deleteError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{deleteError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TestDeals;
